import React from "react";
import { Box, Typography, Theme, Tooltip } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { AppLogo } from "../app/AppLogo";
import { Avatar } from "../avatar/Avatar";
import logo from "../app/logo.svg";
import { LibraryPrivacy } from "@growth-machine-llc/stridist-api-client";

interface StyleProps {
  size: number;
  badgePosition?: "top-right" | "bottom-right";
  avatarRadius?: number;
}

const useStyles = makeStyles<Theme, StyleProps>((theme) => ({
  root: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  avatar: {
    width: (props) => props.size,
    height: (props) => props.size,
    borderRadius: theme.spacing(1.5),
    border: `1px solid ${theme.palette.border.primary}`,
    margin: 0,
  },
  avatarBadge: {
    position: "absolute",
    top: 0,
    right: 0,
    transform: "translate(35%, -35%)",
    borderRadius: "50%",
    border: `1px solid ${theme.palette.border.primary}`,
    width: 24,
    height: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    boxShadow: theme.shadows[1],
    margin: 0,
  },
  logoContainer: {
    position: "relative",
    display: "inline-flex",
  },
}));

export interface LibraryPrivacyBadgeProps {
  privacy: LibraryPrivacy;
  owner?: {
    photoUrl?: string;
    displayName?: string;
  };
  tooltip?: string;
  size?: number;
  showLabel?: boolean;
  badgePosition?: "top-right" | "bottom-right";
}

export const privacyTooltip = (
  itemText: string,
  privacy: LibraryPrivacy,
  ownerName?: string,
) => {
  switch (privacy) {
    case LibraryPrivacy.PUBLIC:
      return `Public ${itemText} available to all users`;

    case LibraryPrivacy.PRIVATE:
      return `Private ${itemText} created by you`;

    case LibraryPrivacy.TEAM:
      return ownerName
        ? `Team ${itemText} shared within your workspace, created by ${ownerName}`
        : `Team ${itemText} shared within your workspace`;
  }
};

const getPrivacyLabel = (privacy: LibraryPrivacy): string => {
  switch (privacy) {
    case LibraryPrivacy.PUBLIC:
      return "Public";
    case LibraryPrivacy.PRIVATE:
      return "Private";
    case LibraryPrivacy.TEAM:
      return "Team";
    default:
      return "Unknown";
  }
};

export const LibraryPrivacyBadge = ({
  privacy,
  owner,
  tooltip,
  size = 40,
  showLabel = false,
  badgePosition = "top-right",
}: LibraryPrivacyBadgeProps) => {
  const s = useStyles({ size, badgePosition });

  const content = (
    <Box className={s.root}>
      {privacy === LibraryPrivacy.PUBLIC && (
        <img src={logo} alt="Public" width={size} height={size} />
      )}

      {privacy === LibraryPrivacy.PRIVATE && (
        <Avatar
          photoURL={owner?.photoUrl}
          displayName={owner?.displayName}
          className={s.avatar}
        />
      )}

      {privacy === LibraryPrivacy.TEAM && (
        <Box className={s.logoContainer}>
          <AppLogo
            size={size}
            width={size}
            height={size}
            className={s.avatar}
          />
          {owner && (
            <Avatar
              photoURL={owner.photoUrl}
              displayName={owner.displayName}
              className={s.avatarBadge}
            />
          )}
        </Box>
      )}

      {showLabel && (
        <Typography className={s.label}>{getPrivacyLabel(privacy)}</Typography>
      )}
    </Box>
  );

  return tooltip ? (
    <Tooltip title={tooltip} arrow>
      {content}
    </Tooltip>
  ) : (
    content
  );
};
